'use client'

import { useEffect, useRef, type ReactNode } from 'react'

import {
  useForm,
  FormProvider,
  type DefaultValues,
  type UseFormProps
} from 'react-hook-form'
import type { ZodSchema } from 'zod'

import { zodResolver } from '@hookform/resolvers/zod'

import {
  useResourceContext,
  type CustomAction,
  type RecordType
} from '@/shared/contexts/Resource'
import { cn } from '@/shared/lib/utils'

import { useSubmitHandler } from './useSubmitHandler'

type ResourceFormProps<TRecord extends RecordType = RecordType> = {
  resetOnSave?: boolean
  defaultValues?: Partial<TRecord>
  useFormConfig?: UseFormProps<TRecord>
  validationSchema?: ZodSchema<TRecord>
  customAction?: CustomAction
  className?: string
  children: ReactNode
  beforeSave?: (data: TRecord) => Promise<TRecord>
  onSave?: (record: TRecord) => void
  onSubmit?: (data: TRecord) => Promise<any>
  onError?: (error: unknown) => void
  nestedFields?: string[]
  referenceFields?: string[]
}

export function ResourceForm<TRecord extends RecordType = RecordType>({
  beforeSave,
  onSave,
  onSubmit,
  onError,
  resetOnSave = false,
  defaultValues = {},
  useFormConfig = {},
  validationSchema,
  customAction,
  className = '',
  children,
  nestedFields,
  referenceFields
}: ResourceFormProps<TRecord>) {
  const formRef = useRef<HTMLFormElement>(null)
  const { record, isEdit } = useResourceContext<TRecord>()

  const methods = useForm<TRecord>({
    defaultValues: (isEdit
      ? { ...defaultValues, ...record }
      : defaultValues) as DefaultValues<TRecord>,
    shouldUnregister: false,
    shouldFocusError: true,
    criteriaMode: 'all',
    mode: 'onBlur',
    resolver: validationSchema
      ? (zodResolver(validationSchema as any) as UseFormProps<TRecord>['resolver'])
      : undefined,
    ...useFormConfig
  })

  const { handleSubmit } = useSubmitHandler({
    methods: methods as any,
    customFormAction: customAction,
    onSubmit,
    onError,
    beforeSave,
    onSave: onSave as any,
    resetOnSave,
    nestedFields,
    referenceFields
  })

  useEffect(() => {
    if (record && isEdit) {
      methods.reset(record as TRecord)
    }
  }, [record, isEdit, methods])

  return (
    <FormProvider {...methods}>
      <form
        ref={formRef}
        onSubmit={methods.handleSubmit(handleSubmit as any)}
        className={cn('flex flex-col gap-4', className)}
      >
        {children}
      </form>
    </FormProvider>
  )
}
